import { type ComponentProps, Fragment, memo, type ReactNode } from 'react';

import { cn } from '@air/utils-tailwind';

/**
 * This should be in sync with the class name on `BoardStatsBar` below.
 */
export const BOARD_STATS_BAR_HEIGHT = 56;

export type BoardStatsBarStat = {
  id: string;
  label: string;
  value: string;
};

export type BoardStatsBarProps = Pick<ComponentProps<'div'>, 'className'> & {
  actions: ReactNode;
  stats: BoardStatsBarStat[];
};

/**
 * @deprecated Use `BoardBottombar` in `components/v2/components/board/BoardBottombar.tsx` instead.
 */
export const BoardStatsBar = memo(({ actions, className, stats }: BoardStatsBarProps) => {
  return (
    <div
      className={cn(
        'sticky bottom-0 flex h-14 w-full shrink-0 items-center justify-between border-t ads:border-t-default border-t-gray-4 ads:bg-base bg-gray-1 px-3 md:px-12',
        className,
      )}
      data-disableselect
      data-testid="BOARD_STATS_BAR"
    >
      <div className="flex flex-wrap gap-1 font-medium ads:text-metadata text-14 text-gray-9">
        {/* biome-ignore lint/suspicious/noUnnecessaryConditions: stats prop is required but optional chaining matches legacy defensive rendering */}
        {stats?.map((stat) => {
          const isLast = stats.indexOf(stat) === stats.length - 1;
          const element = <div key={stat.id}>{stat.label}</div>;

          if (isLast) {
            return element;
          }

          return (
            <Fragment key={stat.id}>
              <span>{element}</span>
              <span>&middot;</span>
            </Fragment>
          );
        })}
      </div>

      {!!actions && <div className="flex shrink-0 gap-2">{actions}</div>}
    </div>
  );
});

BoardStatsBar.displayName = 'BoardStatsBar';
